import { Injectable } from '@angular/core';
import { ApiService } from '@uranda/commons';
import { Subject } from 'rxjs';
import { Profile } from '../../models/profile.model';
import { PartnerStatus } from '../../models/partner.model';
import { HealthDataDeclaration, PartnerPermission, PermissionRequirement } from '../../models/partner-permission.model';
import { isLegacyPartnerProfile } from '../../utils/partner-permissions.util';

@Injectable({
    providedIn: 'root',
})
export class ProfileService extends ApiService {

    profile?: Profile;
    isAdmin = false;
    isPartner = false;
    isSupport = false;

    /**
     * Fired at the end of every successful getProfile() (this.profile/isAdmin/isPartner/isSupport
     * have already been reassigned by then). A rejected getProfile() throws before this fires, so
     * subscribers never see a "reload failed" case - which is fine, since profile/role/permissions
     * are left as they were before the failed call. Anything that builds gated UI once and never
     * re-evaluates it - the sidemenu was the offender (see SidemenuComponent) - should subscribe
     * instead of relying on an unrelated trigger (e.g. counters) to happen to rebuild it too.
     */
    private readonly profileChangedSource = new Subject<void>();
    readonly profileChanged$ = this.profileChangedSource.asObservable();

    async getProfile() {
        const response = await this.get("/v1/account/profile");
        this.profile = response.data
        this.isAdmin = this.profile?.role == 'ADMIN'
        this.isPartner = this.profile?.role == 'PARTNER'
        this.isSupport = this.profile?.role == 'SUPPORT'
        this.profileChangedSource.next();
        return this.profile;
    }

    async updatePassword(currentPassword: string, newPassword: string, confirmPassword: string) {
        const response = await this.put('/v1/account/update-password', { currentPassword, newPassword, confirmPassword });
        return response.data;
    }

    clearProfile() {
        this.profile = undefined;
        this.isAdmin = false;
        this.isPartner = false;
        this.isSupport = false;
    }

    /**
     * Only PARTNER users are subject to clinic permissions; admin/support are never gated.
     * A profile from a backend that predates this feature (no `permissions` at all) is treated
     * as a legacy owner with unrestricted access, rather than locked out by the new gating.
     */
    get isOwner(): boolean {
        if (!this.isPartner) return false;
        if (isLegacyPartnerProfile(this.profile)) return true;
        return this.profile?.isOwner === true;
    }

    hasPermission(permission: PartnerPermission): boolean {
        if (!this.isPartner) return true;
        if (isLegacyPartnerProfile(this.profile)) return true;
        return this.profile?.permissions?.includes(permission) ?? false;
    }

    meets(requirement: PermissionRequirement): boolean {
        if (requirement === 'OWNER') return !this.isPartner || this.isOwner;
        return this.hasPermission(requirement);
    }

    get healthDataEligible(): boolean {
        if (!this.isPartner) return false;
        if (isLegacyPartnerProfile(this.profile)) return false;
        return this.profile?.healthDataEligible === true;
    }

    /** The health-data declaration (modal + Settings tab) only applies to APPROVED clinics. */
    get isPartnerApproved(): boolean {
        return this.profile?.partner?.status === PartnerStatus.APPROVED;
    }

    get needsHealthDeclaration(): boolean {
        if (!this.isPartner) return false;
        if (isLegacyPartnerProfile(this.profile)) return false;
        return this.profile?.needsHealthDeclaration === true;
    }

    async updateHealthDeclaration(value: HealthDataDeclaration) {
        await this.put('/v1/account/health-declaration', { value });
        return this.getProfile();
    }

    /**
     * Destinazione "home" dell'utente in base al ruolo, allineata al redirect
     * post-login (vedi LoginComponent.redirectAfterLogin). Usata dai guard per
     * rimbalzare l'utente al suo pannello invece di lasciare una pagina bianca.
     */
    homeRoute(): string[] {
        switch (this.profile?.role) {
            case 'ADMIN':
                return ['/admin'];
            case 'SUPPORT':
                return ['/support/leads'];
            case 'PARTNER':
                if (this.profile?.partner?.status == PartnerStatus.PENDING && !this.profile.partner.licensePhotoUrl) {
                    return ['/partners/verification'];
                }
                return ['/partners'];
            default:
                return ['/login'];
        }
    }

}